// Catalog.ID Client — Terminating the principal of live agents
// ============================================================================
// docs/cid-governance.md M15. Terminating an identity that is the principal of
// live agents — an account's own termination, a delegate's removal or
// self-termination, an organization's termination — ends those agents for good:
// their private keys live in the departing principal's key bag and cannot move
// to another principal, and everything customers shared with them names their
// keys. So the node refuses such a termination with 409
// `principal_has_live_agents` (listing the agents) unless the request carries
// `terminateAgents: true`, and the client asks the member before it sends that.

import { ApiError } from '../api/client';

/** The agents a 409 `principal_has_live_agents` names, or null for any other error. */
export function liveAgentsOf(err: unknown): string[] | null {
    if (!(err instanceof ApiError) || err.status !== 409) return null;
    const body = err.body as { code?: unknown; agents?: unknown } | null;
    if (typeof body !== 'object' || body === null || body.code !== 'principal_has_live_agents') return null;
    return Array.isArray(body.agents) ? body.agents.filter((a): a is string => typeof a === 'string') : [];
}

/**
 * Run `attempt(false)`; when the node answers `principal_has_live_agents`, ask
 * `confirm` with the agents it named and, on yes, run `attempt(true)`. Each
 * attempt signs its own step-up (a challenge is single-use). Resolves false
 * when the member declined; any other failure propagates.
 */
export async function withAgentAcknowledgement(
    attempt: (terminateAgents: boolean) => Promise<void>,
    confirm: (agents: string[]) => Promise<boolean>,
): Promise<boolean> {
    try {
        await attempt(false);
        return true;
    } catch (err) {
        const agents = liveAgentsOf(err);
        if (agents === null) throw err;
        if (!await confirm(agents)) return false;
        await attempt(true);
        return true;
    }
}

/** The question the member answers before its agents are terminated with it. */
export function liveAgentsMessage(agents: string[], whose: string): string {
    const count = agents.length === 1 ? 'an agent' : `${agents.length} agents`;
    const list = agents.length > 0 ? ` (${agents.join(', ')})` : '';
    return `${whose} the principal of ${count}${list}. They end with it, for good: their keys cannot move `
        + 'to another identity, and whatever was shared with them has to be shared again with a new agent. '
        + 'Terminate them too?';
}
